import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Move PdfModal outside App component to prevent re-rendering issues
const PdfModal = ({ 
  showPdfModal, 
  setShowPdfModal, 
  clientName, 
  setClientName, 
  preparedBy, 
  setPreparedBy, 
  contactNo, 
  setContactNo, 
  includeParticulars, 
  setIncludeParticulars, 
  generatePDF 
}) => {
  if (!showPdfModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-11/12 sm:w-full max-w-sm border border-gray-100 shadow-xl transform transition-all mx-4">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Generate PDF</h3>
        <p className="text-xs text-gray-500 mb-3">
          Create your customized report. Include particulars below if desired.
        </p>
        
        {/* Checkbox for including particulars */}
        <div className="mb-3">
          <label className="flex items-center space-x-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeParticulars}
              onChange={(e) => setIncludeParticulars(e.target.checked)}
              className="w-3.5 h-3.5 text-blue-600 bg-gray-50 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
            />
            <span className="text-xs font-semibold text-gray-700">
              Add custom details into the report?
            </span>
          </label>
        </div>
        
        {/* Form fields - always visible but conditionally enabled */}
        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-xxs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Client Name (Optional)
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={!includeParticulars}
              className={`w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                !includeParticulars ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white text-gray-950'
              }`}
              placeholder="e.g. Client name"
            />
          </div>
          
          <div>
            <label className="block text-xxs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Prepared By (Your Name)
            </label>
            <input
              type="text"
              value={preparedBy}
              onChange={(e) => setPreparedBy(e.target.value)}
              disabled={!includeParticulars}
              className={`w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                !includeParticulars ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white text-gray-950'
              }`}
              placeholder="e.g. Your name"
            />
          </div>
          
          <div>
            <label className="block text-xxs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Contact No. (Your Contact)
            </label>
            <input
              type="text"
              value={contactNo}
              onChange={(e) => setContactNo(e.target.value)}
              disabled={!includeParticulars}
              className={`w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent ${
                !includeParticulars ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white text-gray-950'
              }`}
              placeholder="e.g. Your contact number"
            />
          </div>
        </div>
        
        <div className="flex flex-col space-y-2">
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPdfModal(false)}
              className="flex-1 py-1.5 px-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-xs font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={() => generatePDF('download')}
              className="flex-1 py-1.5 px-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-xs font-semibold"
            >
              Download PDF
            </button>
          </div>
          <button
            onClick={() => generatePDF('share')}
            className="w-full py-2 px-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-xs font-semibold shadow-sm"
          >
            <img src={`${process.env.PUBLIC_URL}/whatsapp.png`} alt="WhatsApp" className="w-3.5 h-3.5 invert brightness-200" />
            <span>Send to Client (WhatsApp)</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// QR Code Modal Component
const QrModal = ({ showQrModal, setShowQrModal, setShowFullQrImage }) => {
  if (!showQrModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-80 max-w-sm mx-4">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">☕ Buy Me a Coffee</h3>
          <p className="text-sm text-gray-600 mb-4">
            Love this calculator? Your support helps keep it running and improving!
          </p>
          
          {/* QR Code Image - Now clickable */}
          <div className="bg-gray-100 border-2 border-gray-200 rounded-lg p-4 mb-4 relative">
            <img 
              src={`${process.env.PUBLIC_URL}/qr-code.png`}
              alt="DuitNow QR Code" 
              className="w-32 h-32 mx-auto rounded-lg object-cover cursor-pointer hover:opacity-80 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => {
                setShowQrModal(false);
                setShowFullQrImage(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowQrModal(false);
                  setShowFullQrImage(true);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Click to view full size QR code"
              onError={(e) => {
                // Fallback to placeholder if image not found
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            {/* Hover overlay indicator */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
              <div className="opacity-0 hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                <span className="text-xs font-medium text-gray-800">🔍 Click to enlarge</span>
              </div>
            </div>
            {/* Fallback placeholder (hidden by default) */}
            <div className="w-32 h-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
              <div className="text-center">
                <div className="text-4xl mb-2">📱</div>
                <div className="text-xs text-gray-500">QR Code<br />Placeholder</div>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mb-2">
            Scan with your phone's camera or payment app
          </p>
          <p className="text-xs text-blue-600 mb-4 font-medium">
            💡 Click QR code to view full size and save
          </p>
          
          <button
            onClick={() => setShowQrModal(false)}
            className="w-full py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Full QR Image Modal Component
const FullQrImageModal = ({ showFullQrImage, setShowFullQrImage }) => {
  if (!showFullQrImage) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-md w-full">
        {/* Close button */}
        <button
          onClick={() => setShowFullQrImage(false)}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold z-10"
        >
          ✕
        </button>
        
        <div className="bg-white rounded-xl p-6 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">DuitNow QR Code</h3>
          
          {/* Full size QR Code Image */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <img 
              src={`${process.env.PUBLIC_URL}/qr-code.png`}
              alt="DuitNow QR Code - Full Size" 
              className="w-full h-auto mx-auto rounded-lg object-contain max-w-sm"
              onError={(e) => {
                // Fallback to placeholder if image not found
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            {/* Fallback placeholder (hidden by default) */}
            <div className="w-64 h-64 mx-auto bg-gray-200 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
              <div className="text-center">
                <div className="text-6xl mb-4">📱</div>
                <div className="text-sm text-gray-500">QR Code<br />Not Available</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Right-click and "Save Image As" to download, or screenshot this QR code
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowFullQrImage(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  // State management
  const [actualPrice, setActualPrice] = useState('');
  const [markupPrice, setMarkupPrice] = useState('');
  const [bookingOption, setBookingOption] = useState('percentage'); // 'percentage' or 'fixed'
  const [bookingPercentage, setBookingPercentage] = useState('3.24');
  const [customBookingPercentage, setCustomBookingPercentage] = useState('');
  const [fixedBookingAmount, setFixedBookingAmount] = useState('');
  const [interestRate, setInterestRate] = useState('4');
  const [loanTerm, setLoanTerm] = useState('35');
  const [loanPercentage, setLoanPercentage] = useState('90');
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState(false);
  const [loan90Selected, setLoan90Selected] = useState(false);
  const [loan100Selected, setLoan100Selected] = useState(false);
  const [useMarkupPrice, setUseMarkupPrice] = useState(false);

  // Legal fees adjustments
  const [spaFeesAdjustment, setSpaFeesAdjustment] = useState('');
  const [loanFeesAdjustment, setLoanFeesAdjustment] = useState('');

  // Bank loan inclusion options
  const [includeLoanLegalFees, setIncludeLoanLegalFees] = useState(true);
  const [includeValuationFees, setIncludeValuationFees] = useState(true);
  const [includeMRTAInsurance, setIncludeMRTAInsurance] = useState(true);

  // Calculations
  const [results, setResults] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [error, setError] = useState('');

  // PDF Modal state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [clientName, setClientName] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [includeParticulars, setIncludeParticulars] = useState(false);

  // QR Code Modal state
  const [showQrModal, setShowQrModal] = useState(false);
  const [showFullQrImage, setShowFullQrImage] = useState(false);

  // Ref for capturing loan details section
  const loanDetailsRef = useRef(null);

  // Helper function to format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    // Remove any non-digit characters except decimal point
    const numericValue = value.toString().replace(/[^\d.]/g, '');
    // Format with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to get numeric value from formatted string
  const getNumericValue = (formattedValue) => {
    return formattedValue.replace(/,/g, '');
  };

  // Handlers for formatted price inputs
  const handleActualPriceChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, '');
    if (rawValue === '' || /^\d+$/.test(rawValue)) {
      setActualPrice(rawValue);
    }
  };

  const handleMarkupPriceChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, '');
    if (rawValue === '' || /^\d+$/.test(rawValue)) {
      setMarkupPrice(rawValue);
    }
  };

  // Malaysian stamp duty rates and calculations
  const calculateStampDuty = (price) => {
    let stampDuty = 0;
    if (price <= 100000) {
      stampDuty = price * 0.01;
    } else if (price <= 500000) {
      stampDuty = 1000 + (price - 100000) * 0.02;
    } else if (price <= 1000000) {
      stampDuty = 1000 + 8000 + (price - 500000) * 0.03;
    } else {
      stampDuty = 1000 + 8000 + 15000 + (price - 1000000) * 0.04;
    }
    return stampDuty;
  };

  const calculateLegalFees = (loanAmount) => {
    let legalFees = 0;
    if (loanAmount <= 150000) {
      legalFees = Math.max(loanAmount * 0.01, 500);
    } else if (loanAmount <= 1000000) {
      legalFees = 1500 + (loanAmount - 150000) * 0.008;
    } else {
      legalFees = 1500 + 6800 + (loanAmount - 1000000) * 0.007;
    }
    return legalFees;
  };

  const calculateSpaLegalFees = (price) => {
    // Updated SPA Legal Fees based on standard Malaysian SRO tiers
    let legalFees = 0;
    if (price > 7500000) {
      // For above RM7.5M, 0.4% on excess (negotiable min)
      legalFees = (500000 * 0.01) + (500000 * 0.008) + (2000000 * 0.006) + (2000000 * 0.005) + (2500000 * 0.004) + ((price - 7500000) * 0.004);
    } else if (price > 5000000) {
      legalFees = (500000 * 0.01) + (500000 * 0.008) + (2000000 * 0.006) + (2000000 * 0.005) + ((price - 5000000) * 0.004);
    } else if (price > 3000000) {
      legalFees = (500000 * 0.01) + (500000 * 0.008) + (2000000 * 0.006) + ((price - 3000000) * 0.005);
    } else if (price > 1000000) {
      legalFees = (500000 * 0.01) + (500000 * 0.008) + ((price - 1000000) * 0.006);
    } else if (price > 500000) {
      legalFees = (500000 * 0.01) + ((price - 500000) * 0.008);
    } else {
      legalFees = price * 0.01;
    }
    legalFees = Math.max(legalFees, 500); // Minimum fee
    const sst = legalFees * 0.06; // 6% SST on legal fees
    const disbursements = 2000; // Fixed disbursements (adjusted to make more realistic)
    const baseTotal = legalFees + sst + disbursements + 3000; // Added RM 3,000 default addon by user request
    const adjustment = parseFloat(spaFeesAdjustment) || 0;
    return baseTotal + adjustment;
  };

  const calculateLoanLegalFees = (loanAmount) => {
    // Loan Agreement Legal Fees + Stamp Duty
    const legalFees = calculateLegalFees(loanAmount);
    const loanStampDuty = loanAmount * 0.005; // 0.5% of loan amount
    const baseTotal = legalFees + loanStampDuty + 1000 + 3000; // Add RM 1,000 for miscellaneous disbursement fees and RM 3,000 default addon
    const adjustment = parseFloat(loanFeesAdjustment) || 0;
    return baseTotal + adjustment;
  };

  const calculateValuationFees = (price) => {
    // Malaysian bank valuation fees based on tiered percentage structure
    let fee = 0;
    
    if (price <= 100000) {
      // First RM 100,000 at 0.25%
      fee = price * 0.0025;
    } else if (price <= 500000) {
      // First RM 100,000 at 0.25% + next RM 400,000 at 0.20%
      fee = (100000 * 0.0025) + ((price - 100000) * 0.002);
    } else if (price <= 1000000) {
      // First RM 100,000 at 0.25% + next RM 400,000 at 0.20% + next RM 500,000 at 0.167%
      fee = (100000 * 0.0025) + (400000 * 0.002) + ((price - 500000) * 0.00167);
    } else if (price <= 3000000) {
      // Up to RM 1M + next RM 2,000,000 at 0.125%
      fee = (100000 * 0.0025) + (400000 * 0.002) + (500000 * 0.00167) + ((price - 1000000) * 0.00125);
    } else if (price <= 5000000) {
      // Up to RM 3M + next RM 2,000,000 at 0.10%
      fee = (100000 * 0.0025) + (400000 * 0.002) + (500000 * 0.00167) + (2000000 * 0.00125) + ((price - 3000000) * 0.001);
    } else if (price <= 10000000) {
      // Up to RM 5M + next RM 5,000,000 at 0.067%
      fee = (100000 * 0.0025) + (400000 * 0.002) + (500000 * 0.00167) + (2000000 * 0.00125) + (2000000 * 0.001) + ((price - 5000000) * 0.00067);
    } else {
      // Above RM 10M - use 0.05% (negotiable, but use max reference)
      fee = (100000 * 0.0025) + (400000 * 0.002) + (500000 * 0.00167) + (2000000 * 0.00125) + (2000000 * 0.001) + (5000000 * 0.00067) + ((price - 10000000) * 0.0005);
    }
    
    return Math.min(Math.max(fee, 1000), 2500); // Clamped between RM 1000 and RM 2500 by user request
  };

  const calculateMRTA = (loanAmount) => {
    // MRTA/MRTT Premium calculation: 2% to 5% of loan amount
    // Using 3.5% as a middle estimate for general calculation
    // Actual rate depends on age, health, loan term, and insurance provider
    const premiumRate = 0.035; // 3.5% as middle estimate
    return loanAmount * premiumRate;
  };

  const calculateMOT = (price) => {
    // Memorandum of Transfer - typically 0.3% of property value
    return price * 0.003;
  };

  // Calculate booking fee based on selected option
  const calculateBookingFee = (price) => {
    if (bookingOption === 'percentage') {
      const percentage = bookingPercentage === 'custom' ? parseFloat(customBookingPercentage) : parseFloat(bookingPercentage);
      return price * (percentage / 100);
    } else {
      return parseFloat(fixedBookingAmount) || 0;
    }
  };

  const getBookingFeeDescription = () => {
    if (bookingOption === 'percentage') {
      const percentage = bookingPercentage === 'custom' ? customBookingPercentage : bookingPercentage;
      return `${percentage}% of property price`;
    } else {
      return 'Fixed amount';
    }
  };

  useEffect(() => {
    // Clear any existing errors first
    let newError = '';
    
    // Check if mark-up toggle is enabled but no mark-up price is entered
    if (useMarkupPrice && (!markupPrice || markupPrice.trim() === '')) {
      newError = 'Please enter a Mark-up SPA Price when the mark-up option is enabled';
    }

    // Check booking fee validation
    if (!newError && bookingOption === 'fixed' && (!fixedBookingAmount || fixedBookingAmount.trim() === '')) {
      newError = 'Please enter a Fixed Booking Amount when Fixed Amount option is selected';
    }

    if (!newError && bookingOption === 'percentage' && bookingPercentage === 'custom' && (!customBookingPercentage || customBookingPercentage.trim() === '')) {
      newError = 'Please enter a custom percentage when Others option is selected';
    }

    // Check loan term validation
    if (!newError && loanTerm && parseFloat(loanTerm) > 35) {
      newError = 'Loan term cannot exceed 35 years';
    }

    // NOTE: First-time buyer stamp duty waiver limit is NOT a blocking validation
    // Users can still generate reports, they just won't get the waiver benefit if price > RM 500,000
    
    // Check if first-time buyer with 100% loan is selected
    const isFirstTimeBuyer100Loan = isFirstTimeBuyer && loan100Selected;
    
    // Validate actual selling price for 100% loan limit
    if (isFirstTimeBuyer100Loan && actualPrice && !newError) {
      const actualPriceNum = parseFloat(actualPrice);
      if (actualPriceNum > 500000) {
        newError = 'Actual Selling Price cannot exceed RM 500,000 for First-time Buyer 100% loan';
      }
    }
    
    // Check if mark-up price is used and validate against actual price
    if (useMarkupPrice && actualPrice && markupPrice && !newError) {
      const actualPriceNum = parseFloat(actualPrice);
      const markupPriceNum = parseFloat(markupPrice);
      
      if (markupPriceNum <= actualPriceNum) {
        newError = 'Mark-up SPA Price must be higher than Actual Selling Price';
      }
    }
    
    // Check 100% loan limit for mark-up price (both first-time buyer and regular 100% loan)
    if (useMarkupPrice && markupPrice && !newError) {
      const is100PercentLoan = loan100Selected || (!isFirstTimeBuyer && loanPercentage === '100');
      if (is100PercentLoan && parseFloat(markupPrice) > 500000) {
        newError = 'Mark-up SPA Price cannot exceed RM 500,000 for 100% loan';
      }
    }
    
    setError(newError);
  }, [markupPrice, useMarkupPrice, actualPrice, loan100Selected, isFirstTimeBuyer, loanPercentage, bookingOption, fixedBookingAmount, bookingPercentage, customBookingPercentage, loanTerm]);

  // Reset first-time buyer checkboxes when toggle is turned off
  useEffect(() => {
    if (!isFirstTimeBuyer) {
      setLoan90Selected(false);
      setLoan100Selected(false);
    }
  }, [isFirstTimeBuyer]);

  // Update loan percentage when first-time buyer options change
  useEffect(() => {
    if (loan90Selected) {
      setLoanPercentage('90');
    } else if (loan100Selected) {
      setLoanPercentage('100');
    }
  }, [loan90Selected, loan100Selected]);

  const calculateSingleLoan = (loanPercent) => {
    const actualSellingPrice = parseFloat(actualPrice);
    const markupSellingPrice = parseFloat(markupPrice) || actualSellingPrice;
    const rate = parseFloat(interestRate) / 100 / 12;
    const terms = parseFloat(loanTerm) * 12;
    
    if (!actualSellingPrice || actualSellingPrice <= 0) {
      return null;
    }

    if (useMarkupPrice && loanPercent === 100 && markupSellingPrice > 500000) {
      return null;
    }

    // Calculate loan amount based on whether markup is used
    const basePriceForLoan = useMarkupPrice ? markupSellingPrice : actualSellingPrice;
    const loanAmount = basePriceForLoan * (loanPercent / 100);
    
    // Calculate booking fee first
    const bookingFee = calculateBookingFee(actualSellingPrice);
    
    // Calculate balance payable to vendor (always deduct booking fee from actual selling price)
    let balancePayableToVendor = 0;
    let cashback = 0;
    
    if (useMarkupPrice) {
      // With markup pricing: Check if loan covers actual selling price
      if (loanAmount >= actualSellingPrice) {
        // Loan covers the full actual selling price
        // Cashback = loan excess + booking fee (since booking fee is returned)
        cashback = (loanAmount - actualSellingPrice) + bookingFee;
        balancePayableToVendor = 0; // Nothing owed to vendor
      } else {
        // Loan doesn't cover full actual selling price
        const totalPaymentToVendor = bookingFee + loanAmount;
        if (totalPaymentToVendor > actualSellingPrice) {
          cashback = totalPaymentToVendor - actualSellingPrice;
          balancePayableToVendor = 0;
        } else {
          balancePayableToVendor = actualSellingPrice - totalPaymentToVendor;
          cashback = 0;
        }
      }
    } else {
      // Normal case: Calculate if there's overpayment
      const totalPaymentToVendor = bookingFee + loanAmount;
      
      if (totalPaymentToVendor > actualSellingPrice) {
        // Overpayment scenario - return excess as cashback
        cashback = totalPaymentToVendor - actualSellingPrice;
        balancePayableToVendor = 0;
      } else {
        // Normal scenario - calculate remaining balance
      balancePayableToVendor = actualSellingPrice - bookingFee - loanAmount;
        cashback = 0;
      }
    }

    // Ensure balance payable is not negative
    if (balancePayableToVendor < 0) {
      balancePayableToVendor = 0;
    }

    if (loanAmount <= 0) {
      return null;
    }

    // Calculate monthly payment
    const monthlyPayment = loanAmount * (rate * Math.pow(1 + rate, terms)) / (Math.pow(1 + rate, terms) - 1);
    
    // Calculate costs
    const effectivePrice = Math.max(actualSellingPrice, markupSellingPrice);
    const isStampDutyWaived = isFirstTimeBuyer && effectivePrice <= 500000;
    const originalStampDuty = calculateStampDuty(effectivePrice);  // Calculate original for display
    const spaStampDuty = isStampDutyWaived ? 0 : originalStampDuty;
    const spaLegalFees = calculateSpaLegalFees(effectivePrice);
    const loanLegalFees = calculateLoanLegalFees(loanAmount);
    const valuationFees = calculateValuationFees(actualSellingPrice);
    const mrtaInsurance = calculateMRTA(loanAmount);
    
    // Calculate bank financed total based on selected options
    let bankFinancedTotal = loanAmount;
    if (includeLoanLegalFees) bankFinancedTotal += loanLegalFees;
    if (includeValuationFees) bankFinancedTotal += valuationFees;
    if (includeMRTAInsurance) bankFinancedTotal += mrtaInsurance;
    
    // Calculate totals
    const spaLawyerTotal = spaLegalFees + spaStampDuty;
    const totalUpfront = bookingFee + balancePayableToVendor + spaLawyerTotal;

    return {
      actualSellingPrice,
      markupSellingPrice: useMarkupPrice ? markupSellingPrice : null,
      loanAmount,
      loanPercentage: loanPercent,
      bookingFee,
      balancePayableToVendor,
      cashback,
      monthlyPayment,
      spaStampDuty,
      spaLegalFees,
      loanLegalFees,
      valuationFees,
      mrtaInsurance,
      spaLawyerTotal,
      bankFinancedTotal,
      totalUpfront,
      loanToValue: loanPercent,
      interestRate: parseFloat(interestRate),
      loanTerm: parseFloat(loanTerm),
      bookingFeeDescription: getBookingFeeDescription(),
      includeLoanLegalFees,
      includeValuationFees,
      includeMRTAInsurance,
      priceForStampDuty: effectivePrice,
      originalStampDuty,
      useMarkupPrice
    };
  };

  const calculateLoan = () => {
    const price = parseFloat(actualPrice);
    
    if (!price || price <= 0) {
      setError('Please enter a valid property price');
      return;
    }

    // Check if mark-up toggle is enabled but no mark-up price is entered
    if (useMarkupPrice && (!markupPrice || markupPrice.trim() === '')) {
      setError('Please enter a Mark-up SPA Price when the mark-up option is enabled');
      return;
    }

    // Check booking fee validation
    if (bookingOption === 'fixed' && (!fixedBookingAmount || fixedBookingAmount.trim() === '')) {
      setError('Please enter a Fixed Booking Amount when Fixed Amount option is selected');
      return;
    }

    if (bookingOption === 'percentage' && bookingPercentage === 'custom' && (!customBookingPercentage || customBookingPercentage.trim() === '')) {
      setError('Please enter a custom percentage when Others option is selected');
      return;
    }

    // Check loan term validation
    if (loanTerm && parseFloat(loanTerm) > 35) {
      setError('Loan term cannot exceed 35 years');
      return;
    }

    // NOTE: First-time buyer stamp duty waiver limit is NOT a blocking validation
    // Users can still generate reports, they just won't get the waiver benefit if price > RM 500,000

    const is100PercentLoan = loan100Selected || (!isFirstTimeBuyer && loanPercentage === '100');
    if (useMarkupPrice && is100PercentLoan && parseFloat(markupPrice) > 500000) {
      setError('Mark-up SPA Price cannot exceed RM 500,000 for 100% loan');
      return;
    }

    // Single loan calculation (no more comparison mode)
      const loanPercent = parseFloat(loanPercentage);
      const singleResult = calculateSingleLoan(loanPercent);
      
      if (singleResult) {
        setResults(singleResult);
        setComparisonResults(null);
        
        // Auto-scroll to results section after calculation (especially useful for mobile)
        setTimeout(() => {
          if (loanDetailsRef.current) {
            loanDetailsRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100); // Small delay to ensure state update is complete
        
      } else {
        setError('Please check your inputs and try again');
        return;
    }

    setError('');
  };

  const resetForm = () => {
    setActualPrice('');
    setMarkupPrice('');
    setBookingOption('percentage');
    setBookingPercentage('3.24');
    setCustomBookingPercentage('');
    setFixedBookingAmount('');
    setInterestRate('4');
    setLoanTerm('35');
    setLoanPercentage('90');
    setIsFirstTimeBuyer(false);
    setLoan90Selected(false);
    setLoan100Selected(false);
    setUseMarkupPrice(false);
    setIncludeLoanLegalFees(true);
    setIncludeValuationFees(true);
    setIncludeMRTAInsurance(true);
    setSpaFeesAdjustment('');
    setLoanFeesAdjustment('');
    setResults(null);
    setComparisonResults(null);
    setError('');
    setClientName('');
    setPreparedBy('');
    setContactNo('');
    setIncludeParticulars(false);
  };

  const formatCurrency = (amount, showDecimals = false) => {
    if (showDecimals) {
      // Show exact amount with 2 decimal places
      return new Intl.NumberFormat('en-MY', {
        style: 'currency',
        currency: 'MYR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } else {
      // Round up the amount and remove decimals (existing behavior)
      const roundedAmount = Math.ceil(amount);
      return new Intl.NumberFormat('en-MY', {
        style: 'currency',
        currency: 'MYR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedAmount);
    }
  };

  const ToggleSwitch = ({ checked, onChange, label }) => (
    <div className="flex items-center space-x-3">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const Badge = ({ children, variant = 'default' }) => {
    const variants = {
      default: 'bg-gray-100 text-gray-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${variants[variant]}`}>
        {children}
      </span>
    );
  };

  const isLoanPercentageDisabled = () => {
    return isFirstTimeBuyer && (loan90Selected || loan100Selected);
  };

  const CostBreakdownSection = ({ data, title }) => (
    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 pdf-hide-title">{title}</h2>
        <button
          onClick={() => setShowPdfModal(true)}
          className="pdf-button px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
        >
          <span>📄</span>
          <span>Generate PDF</span>
        </button>
      </div>
      
      {/* Monthly Payment Highlight */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-6">
        <h3 className="font-semibold text-green-800">Monthly Payment</h3>
        <p className="text-2xl font-bold text-green-900">
          {formatCurrency(data.monthlyPayment)}
        </p>
        <p className="text-sm text-green-700">
          {data.interestRate}% p.a. over {data.loanTerm} years
        </p>
        
        {/* Property & Loan Details */}
        <div className="mt-3 pt-3 border-t border-green-300">
          <div className="grid grid-cols-2 gap-3 text-xs text-green-800">
            <div>
              <span className="font-medium">Property Price:</span>
              <br />
              <span className="font-bold">{formatCurrency(data.actualSellingPrice)}</span>
            </div>
            <div>
              <span className="font-medium">Loan Amount ({data.loanToValue}%):</span>
              <br />
              <span className="font-bold">{formatCurrency(data.loanAmount)}</span>
            </div>
            {data.markupSellingPrice && (
              <>
                <div className="col-span-2">
                  <span className="font-medium">Mark-up SPA Price:</span>
                  <span className="font-bold ml-2">{formatCurrency(data.markupSellingPrice)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cashback Alert (if applicable) */}
      {data.cashback > 0 && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
          <h3 className="font-semibold text-blue-800">💰 Cashback Available</h3>
          <p className="text-xl font-bold text-blue-900">
            {formatCurrency(data.cashback, true)}
          </p>
          <p className="text-xs text-blue-700">
            {data.markupSellingPrice ? 'Excess from mark-up SPA price (includes booking fee refund)' : 'Overpayment refund'}
          </p>
        </div>
      )}

      {/* Two-column layout for PDF, single column for web */}
      <div className="pdf-two-column-container">
        <div className="pdf-left-column">
          {/* 1. Booking Fee */}
          <div className="border-l-4 border-orange-400 pl-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">1) Booking Fee ({data.bookingFeeDescription})</h3>
            <p className="text-lg font-bold text-orange-600">{formatCurrency(data.bookingFee, true)}</p>
            <p className="text-xs text-gray-600 mt-1">
              Initial payment to secure the property purchase
            </p>
          </div>

          {/* 2. Balance Payable to Vendor */}
          <div className="border-l-4 border-blue-400 pl-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-2">2) Balance Payable to Vendor</h3>
            <p className="text-lg font-bold text-blue-600">{formatCurrency(data.balancePayableToVendor, true)}</p>
            {data.balancePayableToVendor > 0 ? (
              <p className="text-xs text-gray-600 mt-1">
                After deducting booking fee ({formatCurrency(data.bookingFee, true)}). Can use EPF Account 2 withdrawal or flexible payment arrangement
              </p>
            ) : (
              <p className="text-xs text-green-600 mt-1">
                {data.cashback > 0 
                  ? data.markupSellingPrice
                    ? `Loan covers full property price after booking fee`
                    : `Booking fee + loan amount exceeds property price. ${formatCurrency(data.cashback, true)} overpayment returned as cashback`
                  : 'Loan amount covers remaining balance after booking fee deduction'
                }
              </p>
            )}
          </div>
        </div>

        <div className="pdf-right-column">
          {/* 3. Payable to SPA Lawyer */}
          <div className="border-l-4 border-purple-400 pl-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">3) Payable to SPA Lawyer</h3>
            <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs text-yellow-800 font-medium">
                ⚠️ Estimated figures: Actual legal fees may vary depending on lawyer rates, complexity of transaction, and current government rates.
              </p>
            </div>
            {/* Informational notice for first-time buyers who don't get stamp duty waiver */}
            {isFirstTimeBuyer && data.priceForStampDuty > 500000 && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800 font-medium">
                  ℹ️ First-time buyer stamp duty waiver only applies to properties RM 500,000 and below. 
                  Current {data.useMarkupPrice ? 'mark-up' : 'actual'} price: RM {data.priceForStampDuty.toLocaleString('en-MY')}. 
                  Normal stamp duty rates apply.
                </p>
              </div>
            )}
            <div className="ml-4 space-y-2">
              <div className="flex justify-between">
                <span>SPA Legal Fees:</span>
                <span className="font-semibold">{formatCurrency(data.spaLegalFees)}</span>
              </div>
              <div className="flex justify-between">
                <span className={isFirstTimeBuyer && data.priceForStampDuty <= 500000 ? 'text-gray-400' : ''}>
                  {isFirstTimeBuyer && data.priceForStampDuty <= 500000 ? (
                    <span style={{ textDecoration: 'line-through' }}>SPA Stamp Duty:</span>
                  ) : (
                    'SPA Stamp Duty:'
                  )}
                </span>
                <div className="text-right">
                  <div className={`font-semibold ${isFirstTimeBuyer && data.priceForStampDuty <= 500000 ? 'text-gray-400' : ''}`}>
                    {isFirstTimeBuyer && data.priceForStampDuty <= 500000 ? (
                      <span style={{ textDecoration: 'line-through' }}>{formatCurrency(data.originalStampDuty)}</span>
                    ) : (
                      formatCurrency(data.spaStampDuty)
                    )}
                  </div>
                  {isFirstTimeBuyer && (
                    <div className="text-xs mt-1">
                      {data.priceForStampDuty <= 500000 ? (
                        <span className="text-green-600">Waived</span>
                      ) : (
                        <span className="text-orange-600">Exceeds RM 500k limit</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-lg font-bold text-purple-600">
                <span>Total:</span>
                <span>{formatCurrency(data.spaLawyerTotal)}</span>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Legal documentation and government duties for property transfer
            </p>
          </div>

          {/* 4. Financed in Bank Loan */}
          <div className="border-l-4 border-green-400 pl-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">4) Financed in Bank Loan</h3>
            <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded">
              <p className="text-xs text-orange-800 font-medium">
                ⚠️ Estimated figures: Bank fees, valuation costs, and insurance premiums may differ based on bank policies, property location, and individual circumstances.
              </p>
            </div>
            <div className="ml-4 space-y-2">
              <div className="flex justify-between">
                <span>Loan Amount ({data.loanToValue}%):</span>
                <span className="font-semibold">{formatCurrency(data.loanAmount)}</span>
              </div>
              {data.markupSellingPrice && (
                <div className="text-xs text-gray-500 italic ml-4">
                  * Loan calculated based on mark-up SPA price
                </div>
              )}
              <div className="flex justify-between">
                <span>Loan Agreement Legal Fees:</span>
                {data.includeLoanLegalFees ? (
                <span className="font-semibold">{formatCurrency(data.loanLegalFees)}</span>
                ) : (
                  <span className="text-sm italic text-gray-500">Pay Separately</span>
                )}
              </div>
              <div className="flex justify-between">
                <span>Valuation Fees:</span>
                {data.includeValuationFees ? (
                <span className="font-semibold">{formatCurrency(data.valuationFees)}</span>
                ) : (
                  <span className="text-sm italic text-gray-500">Pay Separately</span>
                )}
              </div>
              <div className="flex justify-between">
                <span>MRTA / MRTT Insurance:</span>
                {data.includeMRTAInsurance ? (
                <span className="font-semibold">{formatCurrency(data.mrtaInsurance)}</span>
                ) : (
                  <span className="text-sm italic text-gray-500">Opted-out</span>
                )}
              </div>
              <hr className="my-2" />
              <div className="flex justify-between text-lg font-bold text-green-600">
                <span>Total:</span>
                <span>{formatCurrency(data.bankFinancedTotal)}</span>
              </div>
            </div>
            <div className="text-xs text-gray-600 mt-2 space-y-1">
              {data.includeMRTAInsurance && (
                <p>• Insurance premium varies by age, health, and loan term - figures shown are estimates only</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary - Full Width */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h3 className="font-semibold text-gray-900 mb-2">Total Upfront Cash Required</h3>
            <p className="text-2xl font-bold text-gray-800">{formatCurrency(data.totalUpfront, true)}</p>
            <p className="text-sm text-gray-600">
              {data.balancePayableToVendor > 0 
                ? "Booking fee + Balance to vendor + SPA lawyer fees"
                : "Booking fee + SPA lawyer fees"
              }
            </p>
          </div>
          
          {/* PDF Button - alongside on desktop, below on mobile/tablet */}
          <div className="lg:ml-4">
            <button
              onClick={() => setShowPdfModal(true)}
              className="pdf-button px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <span>📄</span>
              <span>Generate PDF</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Function to log PDF generation to server with comprehensive error handling
  const logPdfGeneration = async () => {
    // Don't block PDF generation if logging fails
    try {
      // Prepare data to send to server - ONLY USER INFO AND TECHNICAL DATA
      const logData = {
        clientName: clientName || null,
        preparedBy: preparedBy || null,
        contactNo: contactNo || null,
        includeParticulars: includeParticulars,
        timestamp: new Date().toISOString()
      };

      // Set a timeout for the logging request (5 seconds max)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Send to logging endpoint (update URL with your domain)
      const response = await fetch('/log_pdf_generation.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logData),
        signal: controller.signal
      });

      // Clear the timeout if request completes
      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        console.log('PDF generation logged successfully:', result);
      } else {
        // Log warning but don't throw error
        console.warn('Failed to log PDF generation:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
      }
    } catch (error) {
      // Comprehensive error handling - never throw or block PDF generation
      if (error.name === 'AbortError') {
        console.warn('PDF logging request timed out (this is normal and does not affect PDF generation)');
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn('PDF logging: Network error or endpoint not available (PDF generation continues normally)');
      } else if (error.name === 'SyntaxError') {
        console.warn('PDF logging: Server returned invalid JSON response (PDF generation continues normally)');
      } else {
        console.warn('PDF logging error (does not affect PDF generation):', {
          name: error.name,
          message: error.message
        });
      }
      
      // Don't show error to user - logging is completely optional
      // PDF generation should always succeed regardless of logging status
    }
  };

  const triggerWhatsappFallback = (fileName, pdf) => {
    // Save file locally so the user gets the document
    pdf.save(fileName);
    
    const reportData = results || comparisonResults?.loan90 || comparisonResults?.loan100;
    if (!reportData) return;

    const message = `🏠 *Malaysian Housing Loan Calculation Report* 💰\n\n` +
      `• *Actual Selling Price:* ${formatCurrency(reportData.actualSellingPrice)}\n` +
      (reportData.useMarkupPrice ? `• *Mark-up SPA Price:* ${formatCurrency(reportData.markupSellingPrice)}\n` : '') +
      `• *Loan Amount:* ${formatCurrency(reportData.loanAmount)} (${reportData.loanPercentage}%)\n` +
      `• *Monthly Payment:* ${formatCurrency(reportData.monthlyPayment)} (${reportData.interestRate}% over ${reportData.loanTerm} years)\n` +
      `• *Total Upfront Cash Required:* ${formatCurrency(reportData.totalUpfront)}\n\n` +
      `*Estimated Lawyer Fees & Stamp Duty:*\n` +
      `• SPA Lawyer Total (Fees + Stamp Duty): ${formatCurrency(reportData.spaLawyerTotal)}\n` +
      `• Loan Lawyer Total (Financed in Loan): ${formatCurrency(reportData.bankFinancedTotal)}\n\n` +
      `_Detailed PDF report downloaded to your device. Calculated via LoanRumah.my_`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setShowPdfModal(false);
  };

  const generatePDF = async (mode = 'download') => {
    if (!results && !comparisonResults) {
      alert('Please calculate a loan first before generating PDF');
      return;
    }
    
    if (!loanDetailsRef.current) {
      alert('Loan details section not found');
      return;
    }

    const element = loanDetailsRef.current;
    
    // Store original styles and scroll positions to restore later
    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;
    const originalMinWidth = element.style.minWidth;
    const originalPosition = element.style.position;
    const originalScrollY = window.scrollY;
    const originalScrollX = window.scrollX;

    try {
      // Log PDF generation (completely non-blocking and error-safe)
      // Use setTimeout to ensure this runs asynchronously and doesn't block PDF generation
      setTimeout(() => {
        logPdfGeneration().catch(err => {
          // Extra safety net - this should never be reached due to error handling in logPdfGeneration
          console.warn('Unexpected error in PDF logging (ignored):', err);
        });
      }, 0);

      // Add PDF-specific styles for two-column layout
      const pdfStyles = document.createElement('style');
      pdfStyles.id = 'pdf-specific-styles';
      pdfStyles.textContent = `
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
        .max-w-5xl {
          max-width: none !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .pdf-two-column-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .pdf-left-column,
        .pdf-right-column {
          display: flex;
          flex-direction: column;
        }
        
        .pdf-left-column > div,
        .pdf-right-column > div {
          flex: 1;
          width: 100%;
        }
        
        /* Stretch containers to full width with optimized padding */
        .pdf-left-column .border-l-4,
        .pdf-right-column .border-l-4 {
          height: fit-content;
          width: 100%;
          padding-left: 10px;
          padding-right: 8px;
          padding-top: 8px;
          padding-bottom: 8px;
        }
        
        /* Maximize content width within containers */
        .pdf-two-column-container .ml-4 {
          margin-left: 16px;
          margin-right: 16px;
        }
        
        /* Adjust spacing for better utilization */
        .pdf-two-column-container .mb-6 {
          margin-bottom: 20px;
        }
        
        .pdf-two-column-container .mb-3 {
          margin-bottom: 12px;
        }
        
        .pdf-two-column-container .mt-3 {
          margin-top: 12px;
        }
        
        .pdf-two-column-container .mt-1 {
          margin-top: 6px;
        }
        
        .pdf-two-column-container .mt-2 {
          margin-top: 10px;
        }
        
        .pdf-two-column-container .space-y-2 > * + * {
          margin-top: 10px;
        }
        
        .pdf-two-column-container .space-y-1 > * + * {
          margin-top: 6px;
        }
        
        /* Adjust font sizes for better PDF readability */
        .pdf-two-column-container h3 {
          font-size: 16px;
          line-height: 1.3;
          margin-bottom: 12px;
          font-weight: 600;
        }
        
        .pdf-two-column-container p {
          font-size: 13px;
          line-height: 1.4;
        }
        
        .pdf-two-column-container .text-lg {
          font-size: 18px;
          line-height: 1.3;
        }
        
        .pdf-two-column-container .text-xs {
          font-size: 11px;
          line-height: 1.3;
        }
        
        .pdf-two-column-container .text-sm {
          font-size: 12px;
          line-height: 1.3;
        }
        
        /* Optimize warning boxes for PDF with better padding */
        .pdf-two-column-container .bg-yellow-50,
        .pdf-two-column-container .bg-blue-50,
        .pdf-two-column-container .bg-orange-50 {
          padding: 10px 12px;
          margin-bottom: 12px;
          border-radius: 4px;
        }
        
        /* Stretch flex containers to full width with better spacing */
        .pdf-two-column-container .flex.justify-between {
          width: 100%;
          padding-left: 0;
          padding-right: 0;
        }
        
        /* Improve spacing for the detailed breakdown sections */
        .pdf-two-column-container .flex.justify-between span {
          display: block;
          word-wrap: break-word;
        }
        
        /* Better spacing for horizontal rules */
        .pdf-two-column-container hr {
          margin: 10px 0;
          border-color: #e5e7eb;
        }
        
        /* Optimize text alignment and spacing */
        .pdf-two-column-container .text-right {
          text-align: right;
          min-width: fit-content;
        }
        
        /* Improve the overall container spacing */
        .pdf-two-column-container .border-l-4 {
          border-left-width: 4px;
          border-left-style: solid;
          background-color: rgba(255, 255, 255, 0.8);
        }
        
        /* Optimize the main content container for maximum width usage */
        .pdf-two-column-container {
          max-width: 100%;
          width: 100%;
        }
        
        /* Ensure the parent container uses full width during PDF capture */
        .bg-white.rounded-xl.shadow-md {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          box-shadow: none !important;
        }
        
        /* Force the main container to use full viewport width during PDF capture */
        .bg-white.rounded-xl.shadow-md {
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        
        /* Make the monthly payment section wider */
        .pdf-two-column-container .bg-green-50 {
          padding: 16px 20px;
          margin-bottom: 20px;
        }
        
        /* Optimize the summary section */
        .pdf-two-column-container .bg-gray-50 {
          padding: 16px 20px;
          margin-top: 20px;
        }
        
        /* Ensure maximum width utilization for the captured element */
        .bg-white.rounded-xl.shadow-md {
          transform: scale(1.0);
          transform-origin: center;
        }

        /* In pdfStyles.textContent, after the .pdf-two-column-container definition, add: */
        .flex.flex-col.lg\:flex-row {
          flex-direction: row !important;
          align-items: center !important;
          justify-content: space-between !important;
        }
        .grid-cols-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
        @media (max-width: 1023px) {
          .pdf-two-column-container {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        .bg-white.rounded-xl.shadow-md {
          min-width: 1024px !important;
        }
      `;
      document.head.appendChild(pdfStyles);

      // Hide all PDF buttons during capture
      const pdfButtons = element.querySelectorAll('.pdf-button');
      pdfButtons.forEach(button => {
        button.style.display = 'none';
      });

      // Hide the title for PDF
      const titleElement = element.querySelector('.pdf-hide-title');
      if (titleElement) {
        titleElement.style.display = 'none';
      }

      // Temporarily style target element and scroll window to (0,0) before capture
      element.style.width = '1024px';
      element.style.maxWidth = '1024px';
      element.style.minWidth = '1024px';
      element.style.position = 'relative';
      window.scrollTo(0, 0);

      // Capture the loan details section as canvas
      const canvas = await html2canvas(element, {
        scale: 2.0, // Reduced from 3.0 to 2.0 to significantly decrease PDF size
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff', // Use white background instead of #f8fafc for cleaner PDF
        logging: false, // Disable logging for cleaner output
        windowWidth: 1280, // Use full window width for better capture
      });

      // Restore original element style and scroll position immediately after capture
      element.style.width = originalWidth;
      element.style.maxWidth = originalMaxWidth;
      element.style.minWidth = originalMinWidth;
      element.style.position = originalPosition;
      window.scrollTo(originalScrollX, originalScrollY);

      // Remove PDF-specific styles
      const addedStyles = document.getElementById('pdf-specific-styles');
      if (addedStyles) {
        addedStyles.remove();
      }

      // Restore PDF buttons and title
      pdfButtons.forEach(button => {
        button.style.display = '';
      });
      if (titleElement) {
        titleElement.style.display = '';
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // Reduced to minimal margin for maximum space utilization
      
      let yPos = 8; // Reduced from 12 to 8

      // Add header information (more compact) - only if checkbox is enabled
      if (includeParticulars && (clientName || preparedBy || contactNo)) {
        pdf.setFontSize(8); // Reduced from 9 to 8
        pdf.setTextColor(107, 114, 128);
        
        let currentX = pageWidth / 2;
        let headerParts = [];
        
        // Build header parts array
        headerParts.push(`Generated: ${new Date().toLocaleDateString('en-MY')}`);
        if (clientName) headerParts.push(`Client: ${clientName}`);
        if (preparedBy) headerParts.push(`Prepared by: ${preparedBy}`);
        if (contactNo) headerParts.push(`Contact: ${contactNo}`);
        
        const headerText = headerParts.join(' | ');
        
        // Calculate starting X position for centered text
        const textWidth = pdf.getTextWidth(headerText);
        currentX = (pageWidth - textWidth) / 2;
        
        // Draw each part with appropriate formatting
        headerParts.forEach((part, index) => {
          if (index > 0) {
            // Add separator
            pdf.setFont('helvetica', 'normal');
            const separatorText = ' | ';
            pdf.text(separatorText, currentX, yPos);
            currentX += pdf.getTextWidth(separatorText);
          }
          
          if (part.includes('Generated:')) {
            // Generated date - normal
            pdf.setFont('helvetica', 'normal');
            pdf.text(part, currentX, yPos);
            currentX += pdf.getTextWidth(part);
          } else {
            // Client details - split label and value
            const [label, value] = part.split(': ');
            
            // Label - normal
            pdf.setFont('helvetica', 'normal');
            const labelText = `${label}: `;
            pdf.text(labelText, currentX, yPos);
            currentX += pdf.getTextWidth(labelText);
            
            // Value - bold
            pdf.setFont('helvetica', 'bold');
            pdf.text(value, currentX, yPos);
            currentX += pdf.getTextWidth(value);
          }
        });
        
        yPos += 6; // Reduced from 8 to 6
      } else if (includeParticulars) {
        // If checkbox is enabled but no details filled, skip header entirely and go straight to disclaimer
        // (No generation date needed)
      }
      // If checkbox is not enabled, skip header entirely

      // Add disclaimer to PDF (always included)
      const disclaimerText = "Important Notice: This report contains estimated figures for planning purposes only. Actual costs may vary based on bank policies, lawyer fees, market conditions, and individual circumstances. Please consult with financial advisors and legal professionals for precise quotations.";
      
      pdf.setFontSize(7); // Reduced from 8 to 7
      pdf.setTextColor(146, 64, 14); // Amber-orange color
      pdf.setFont('helvetica', 'normal');
      
      const disclaimerLines = pdf.splitTextToSize(disclaimerText, pageWidth - (margin * 2));
      disclaimerLines.forEach((line, index) => {
        pdf.text(line, pageWidth / 2, yPos + (index * 2.5), { align: 'center' }); // Reduced from 3 to 2.5
      });
      
      yPos += disclaimerLines.length * 2.5 + 2; // Reduced spacing from 3 to 2

      // Calculate optimal image dimensions to maximize use of available space
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - yPos - 2; // Reduced bottom margin from 3 to 2
      
      // Calculate image dimensions based on canvas aspect ratio
      const canvasAspectRatio = canvas.width / canvas.height;
      
      let imgWidth, imgHeight;
      
      // Maximize width usage - use full available width
      imgWidth = availableWidth;
      imgHeight = imgWidth / canvasAspectRatio;
      
      // If height exceeds available space, fit by height instead
      if (imgHeight > availableHeight) {
        imgHeight = availableHeight;
        imgWidth = imgHeight * canvasAspectRatio;
      }
      
      // Center the image horizontally while using maximum available width
      const imageX = (pageWidth - imgWidth) / 2;
      
      // Add the image to PDF - centered and using maximum available width, compressed to JPEG at 75% quality
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.75), 'JPEG', imageX, yPos, imgWidth, imgHeight, undefined, 'FAST');
    
    // Save/Share the PDF
    const randomNumbers = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    const dateString = new Date().toISOString().split('T')[0];
    const clientPart = clientName ? `_${clientName.replace(/[^a-zA-Z0-9]/g, '')}` : '';
    const fileName = `LoanRumah.my_LoanReport_${randomNumbers}${clientPart}_${dateString}.pdf`;

    if (mode === 'share') {
      const pdfBlob = pdf.output('blob');
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: 'Malaysian Housing Loan Report',
            text: `Here is the housing loan breakdown report for your property purchase.`
          });
          setShowPdfModal(false);
        } catch (shareError) {
          if (shareError.name !== 'AbortError') {
            console.error('Sharing failed, falling back:', shareError);
            triggerWhatsappFallback(fileName, pdf);
          }
        }
      } else {
        // Fallback for browsers without PDF share support
        triggerWhatsappFallback(fileName, pdf);
      }
    } else {
      pdf.save(fileName);
      setShowPdfModal(false);
    }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
      
      // Restore styles and buttons in case of error
      if (loanDetailsRef.current) {
        const element = loanDetailsRef.current;
        element.style.width = originalWidth;
        element.style.maxWidth = originalMaxWidth;
        element.style.minWidth = originalMinWidth;
        element.style.position = originalPosition;
        
        const pdfButtons = element.querySelectorAll('.pdf-button');
        pdfButtons.forEach(button => {
          button.style.display = '';
        });
        
        const titleElement = element.querySelector('.pdf-hide-title');
        if (titleElement) {
          titleElement.style.display = '';
        }
      }
      
      window.scrollTo(originalScrollX, originalScrollY);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4 sm:px-6 md:px-8 overflow-x-hidden" style={{fontSize: '0.875rem'}}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-7">
          {/* Logo */}
          <div className="mb-6">
            <a 
              href="https://loanrumah.my" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
              aria-label="LoanRumah.my - Malaysian Housing Loan Calculator"
            >
              <img 
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="LoanRumah.my - Free Malaysian Housing Loan Calculator Logo" 
                className="mx-auto h-auto object-contain max-w-full"
                style={{maxWidth: '90vw'}}
                onError={(e) => {
                  // Fallback to placeholder if logo not found
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            </a>
            {/* Fallback placeholder (hidden by default) */}
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl border-2 border-dashed border-blue-300 flex items-center justify-center" style={{display: 'none'}}>
              <div className="text-center">
                <div className="text-3xl mb-1">🏠</div>
                <div className="text-xs text-blue-600 font-medium">Logo Here</div>
              </div>
            </div>
          </div>

          {/* Problem Statement & Solution */}
          <div className="max-w-4xl mx-auto mb-5">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Property Agent Mortgage Report Generator
            </h1>
            
            {/* Highlighted REN subtitle with background */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-1 px-4 rounded-full text-center mb-3 shadow-sm">
              <p className="text-xs font-medium">
                Calculate Housing Loan, Stamp Duty, Legal Fees & Generate Professional PDF Reports for Clients
              </p>
            </div>
            
            {/* SEO-friendly description */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-4 text-center border border-blue-200">
              <h2 className="text-xs font-semibold text-gray-800 mb-1">
                Designed for Real Estate Negotiators (RENs) & Property Agents
              </h2>
              <div className="text-xxs text-gray-500 flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-1">
                <span>✓ Custom Legal Fee Offsets</span>
                <span>✓ Stamp Duty Calculation</span>
                <span>✓ Instant WhatsApp Sharing</span>
                <span>✓ Lightweight PDF client reports</span>
              </div>
            </div>
          </div>


        </div>

        <div className="grid lg:grid-cols-2 gap-7">
          {/* Input Form */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">
              Malaysian Housing Loan Calculator - Enter your Client's Details
            </h2>

            {/* First Time Buyer Toggle */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <ToggleSwitch
                checked={isFirstTimeBuyer}
                onChange={setIsFirstTimeBuyer}
                label="First-time Home Buyer"
              />
              
              {isFirstTimeBuyer && (
                <div className="mt-4 space-y-4">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs text-green-800 font-medium mb-1">
                      🎉 First-time Buyer Benefits:
                    </p>
                    <ul className="text-xs text-green-700 space-y-1">
                      <li>• SPA Stamp Duty waived (for properties RM 500,000 and below)</li>
                      <li>• Access to 90% and 100% loan options</li>
                      <li>• 100% loan available for properties up to RM 500,000</li>
                    </ul>
                    <p className="text-xs text-green-600 mt-2 italic">
                      💡 Properties above RM 500,000 will have normal stamp duty rates but can still proceed with calculation.
                    </p>
                  </div>
                  
                  <p className="text-sm text-blue-700 font-medium">
                    Select your preferred loan option:
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium">90% Loan</span>
                        <Badge variant="success">Popular</Badge>
                      </div>
                      <ToggleSwitch
                        checked={loan90Selected}
                        onChange={(checked) => {
                          setLoan90Selected(checked);
                          if (checked) {
                            setLoan100Selected(false);
                            setLoanPercentage('90');
                          }
                        }}
                        label=""
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium">100% Loan</span>
                        <Badge variant="warning">Max RM500k</Badge>
                      </div>
                      <ToggleSwitch
                        checked={loan100Selected}
                        onChange={(checked) => {
                          setLoan100Selected(checked);
                          if (checked) {
                            setLoan90Selected(false);
                            setLoanPercentage('100');
                          }
                        }}
                        label=""
                      />
                  </div>
                  </div>
                  
                  {/* Additional guidance text */}
                  <p className="text-xs text-blue-600 mt-3 italic">
                    💡 Want a different percentage? You can customize it manually in the "Loan Percentage" field below.
                  </p>
                  
                  {(loan90Selected || loan100Selected) && (
                    <p className="text-xs text-blue-700 mt-2">
                      ✓ {loan90Selected ? '90%' : '100%'} loan option selected
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Mark-up SPA Price Toggle */}
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <ToggleSwitch
                checked={useMarkupPrice}
                onChange={setUseMarkupPrice}
                label="Use Mark-up SPA Price"
              />
              {useMarkupPrice && (
                <>
                  <p className="text-xs text-yellow-700 mt-2">
                    Loan amount will be calculated based on mark-up price. Excess over actual price becomes cashback.
                  </p>
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    ⚠️ Disclaimer: Mark-up SPA pricing is presented for educational and illustrative purposes only. This calculator does not endorse or recommend any practices that may violate Malaysian property laws or regulations. Always consult qualified legal professionals for advice on property transactions.
                  </p>
                </>
              )}
            </div>

            {/* Property Prices */}
            <div className="space-y-4 mb-6">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-lg mr-2">🏠</span>
                  <label className="block text-xs font-semibold text-blue-800 uppercase tracking-wider mb-1.5">
                    Actual Selling Price (RM)
                    {isFirstTimeBuyer && loan100Selected && (
                      <span className="text-red-600 text-xxs ml-2 font-normal normal-case">
                        (Max: RM 500,000 for 100% loan)
                      </span>
                    )}
                  </label>
                </div>
                <input
                  type="text"
                  value={formatNumberWithCommas(actualPrice)}
                  onChange={handleActualPriceChange}
                  max={isFirstTimeBuyer && loan100Selected ? 500000 : undefined}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:border-transparent text-blue-900 font-medium ${
                    isFirstTimeBuyer && loan100Selected && actualPrice && parseFloat(actualPrice) > 500000
                      ? 'border-red-500 bg-red-50 focus:ring-red-500'
                      : 'border-blue-300 bg-white focus:ring-blue-500'
                  }`}
                  placeholder="e.g., 400,000"
                />
                <div className="mt-1 space-y-1">
                  <p className="text-xs text-blue-600">
                  📋 Official property purchase price
                </p>
                  {isFirstTimeBuyer && loan100Selected && actualPrice && parseFloat(actualPrice) > 500000 && (
                    <p className="text-xs text-red-600 font-medium">
                      ⚠️ Exceeds RM 500,000 limit for First-time Buyer 100% loan
                    </p>
                  )}
                </div>
              </div>

              {useMarkupPrice && (
                <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-dashed border-yellow-400 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="text-lg mr-2">💰</span>
                    <label className="block text-xs font-bold text-orange-800 uppercase tracking-wider mb-1.5">
                      Mark-up SPA Price (RM)
                      {(loan100Selected || (!isFirstTimeBuyer && loanPercentage === '100')) && (
                        <span className="text-red-600 text-xxs ml-2 font-normal normal-case">
                          (Max: RM 500,000)
                        </span>
                      )}
                    </label>
                  </div>
                  <input
                    type="text"
                    value={formatNumberWithCommas(markupPrice)}
                    onChange={handleMarkupPriceChange}
                    max={(loan100Selected || (!isFirstTimeBuyer && loanPercentage === '100')) ? 500000 : undefined}
                    className={`w-full px-3 py-2 text-sm border rounded-lg font-semibold focus:ring-2 focus:border-transparent ${
                      error && (error.includes('Mark-up SPA Price') || error.includes('higher than Actual Selling Price'))
                        ? 'border-red-500 bg-red-50 text-red-900 focus:ring-red-500'
                        : actualPrice && markupPrice && parseFloat(markupPrice) > parseFloat(actualPrice)
                          ? 'border-green-400 bg-green-50 text-green-900 focus:ring-green-500'
                          : 'border-orange-400 bg-yellow-50 text-orange-950 focus:ring-orange-500'
                    }`}
                    placeholder="e.g., 480,000 (Higher than actual price)"
                  />
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                    <span className="text-xs text-orange-700 font-medium bg-yellow-200 px-2 py-1 rounded-full text-center sm:text-left">
                      ⚠️ Must be higher than actual price
                    </span>
                    <span className="text-xs text-green-700 font-medium bg-green-200 px-2 py-1 rounded-full text-center sm:text-left">
                      💵 Excess becomes cashback
                    </span>
                    </div>
                    
                    {/* Real-time validation feedback */}
                    {actualPrice && markupPrice && (
                      <div className="space-y-1">
                        {parseFloat(markupPrice) <= parseFloat(actualPrice) && (
                          <p className="text-xs text-red-600 font-medium">
                            ❌ Mark-up price must be higher than actual price (RM {parseFloat(actualPrice).toLocaleString('en-MY')})
                          </p>
                        )}
                        {(loan100Selected || (!isFirstTimeBuyer && loanPercentage === '100')) && parseFloat(markupPrice) > 500000 && (
                          <p className="text-xs text-red-600 font-medium">
                            ❌ Exceeds RM 500,000 limit for 100% loan
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Options */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Booking Fee Options</h3>
              
              {/* Booking Option Tabs */}
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setBookingOption('percentage')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    bookingOption === 'percentage'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Percentage-based
                </button>
                <button
                  onClick={() => setBookingOption('fixed')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    bookingOption === 'fixed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Fixed Amount
                </button>
              </div>

              {bookingOption === 'percentage' && (
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bookingPercentage"
                      value="3.24"
                      checked={bookingPercentage === '3.24'}
                      onChange={(e) => setBookingPercentage(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">3% + SST (3.24%)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bookingPercentage"
                      value="2.70"
                      checked={bookingPercentage === '2.70'}
                      onChange={(e) => setBookingPercentage(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">2.5% + SST (2.70%)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bookingPercentage"
                      value="2.16"
                      checked={bookingPercentage === '2.16'}
                      onChange={(e) => setBookingPercentage(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">2% + SST (2.16%)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bookingPercentage"
                      value="custom"
                      checked={bookingPercentage === 'custom'}
                      onChange={(e) => setBookingPercentage(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Others:</span>
                    {bookingPercentage === 'custom' && (
                      <input
                        type="number"
                        step="0.01"
                        value={customBookingPercentage}
                        onChange={(e) => setCustomBookingPercentage(e.target.value)}
                        className="ml-2 px-2 py-1 border border-gray-300 rounded text-sm w-20"
                        placeholder="0.00"
                      />
                    )}
                    {bookingPercentage === 'custom' && <span className="text-sm ml-1">%</span>}
                  </label>
                </div>
              )}

              {bookingOption === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Amount (RM)
                  </label>
                  <input
                    type="number"
                    value={fixedBookingAmount}
                    onChange={(e) => setFixedBookingAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 15000"
                  />
                </div>
              )}
            </div>

            {/* Loan Percentage */}
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                Loan Percentage (%)
                {isLoanPercentageDisabled() && (
                  <span className="text-gray-500 text-xxs ml-2 normal-case font-normal">(Auto-set by first-time buyer options)</span>
                )}
              </label>
              <input
                type="number"
                value={loanPercentage}
                onChange={(e) => setLoanPercentage(e.target.value)}
                disabled={isLoanPercentageDisabled()}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isLoanPercentageDisabled() ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
                placeholder="e.g., 90"
                min="1"
                max="100"
              />
            </div>

            {/* Interest Rate and Loan Term */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Interest Rate (% p.a.)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder="4"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Loan Term (years)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(e.target.value)}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 ${
                      loanTerm && parseFloat(loanTerm) > 35
                        ? 'border-red-500 bg-red-50 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="35"
                    min="1"
                    max="35"
                  />
                  {loanTerm && parseFloat(loanTerm) > 35 && (
                    <p className="text-xxs text-red-600 font-medium absolute -bottom-4.5 left-0">
                      ⚠️ Max 35 years
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Bank Loan Inclusion Options */}
            <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h3 className="text-sm font-medium text-indigo-800 mb-3">Bank Loan Financing Options</h3>
              <p className="text-xs text-indigo-700 mb-3">Select which items to include in your bank loan financing:</p>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={includeLoanLegalFees}
                    onChange={(e) => setIncludeLoanLegalFees(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">Include Loan Agreement Legal Fees in financing</span>
                </label>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={includeValuationFees}
                    onChange={(e) => setIncludeValuationFees(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">Include Valuation Fees in financing</span>
                </label>
                
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={includeMRTAInsurance}
                    onChange={(e) => setIncludeMRTAInsurance(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700">Include MRTA / MRTT Insurance in financing</span>
                </label>
              </div>
              
            </div>

            {/* Legal Fees Adjustments Card */}
            <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200 shadow-sm">
              <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center">
                <span className="text-base">⚖️</span>
                <span className="ml-2">Adjust Legal Fees (Optional)</span>
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    SPA Legal Fee Offset (RM)
                  </label>
                  <input
                    type="number"
                    value={spaFeesAdjustment}
                    onChange={(e) => setSpaFeesAdjustment(e.target.value)}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white font-medium text-purple-950"
                    placeholder="e.g., 2000"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${
                    !includeLoanLegalFees ? 'text-gray-400' : 'text-gray-700'
                  }`}>
                    Loan Legal Fee Offset (RM)
                  </label>
                  <input
                    type="number"
                    value={loanFeesAdjustment}
                    onChange={(e) => setLoanFeesAdjustment(e.target.value)}
                    disabled={!includeLoanLegalFees}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:border-transparent font-medium ${
                      !includeLoanLegalFees 
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'border-purple-300 bg-white text-purple-950 focus:ring-purple-500'
                    }`}
                    placeholder="e.g., 2000"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={resetForm}
                className="flex-1 py-3 px-6 rounded-lg font-medium transition-colors bg-gray-500 text-white hover:bg-gray-600"
              >
                Reset
              </button>
              <button
                onClick={calculateLoan}
                disabled={!!error}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${
                  error
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Calculate Loan
              </button>
            </div>
          </div>

          {/* Details Section */}
          {results && (
            <div ref={loanDetailsRef}>
            <CostBreakdownSection data={results} title="Loan Details" />
            </div>
          )}

          {comparisonResults && (
            <div className="space-y-6" ref={loanDetailsRef}>
              <CostBreakdownSection 
                data={comparisonResults.loan90} 
                title="90% Loan Details" 
              />
              <CostBreakdownSection 
                data={comparisonResults.loan100} 
                title="100% Loan Details" 
              />
            </div>
          )}

          {!results && !comparisonResults && (
            <div className="bg-white rounded-xl shadow-md p-6 sm:p-12 text-center">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Calculate</h3>
              <p className="text-gray-600">
                Enter your details and click calculate to see comprehensive loan breakdown
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 bg-white border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            
            {/* SEO Footer Content */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left sm:text-center">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">About Property Agent Mortgage Report Generator</h4>
              <p className="text-xs text-gray-600 leading-relaxed mb-3">
                This professional mortgage and cost calculator is designed specifically for real estate negotiators (REN) and property agents in Malaysia. 
                Easily compute client housing loans, SPA and loan legal fees (with custom adjustment offsets), stamp duty, valuation fees, and upfront cash requirements. 
                Instantly generate clean, compressed PDF client reports and share them directly via WhatsApp to simplify your client consultations.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
                <div>✓ Professional PDF Client Reports</div>
                <div>✓ Custom Legal Fee Offsets</div>
                <div>✓ Stamp Duty & MOT Calculator</div>
                <div>✓ Financing Inclusions (MRTA/Valuation)</div>
                <div>✓ First-Time Buyer Exemptions</div>
                <div>✓ Mark-up SPA Cashbacks</div>
                <div>✓ WhatsApp Sharing</div>
                <div>✓ Lightweight PWA Installable App</div>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 space-y-1">
              <p>© 2026 LoanRumah.my - Property Agent Mortgage Report Generator • Created by <a href="https://propertymacha.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium">Vikneswaran Gobalakrishnan | PropertyMacha</a></p>
            </div>
          </div>
        </div>
      </footer>

      <PdfModal 
        showPdfModal={showPdfModal}
        setShowPdfModal={setShowPdfModal}
        clientName={clientName}
        setClientName={setClientName}
        preparedBy={preparedBy}
        setPreparedBy={setPreparedBy}
        contactNo={contactNo}
        setContactNo={setContactNo}
        includeParticulars={includeParticulars}
        setIncludeParticulars={setIncludeParticulars}
        generatePDF={generatePDF}
      />

      <QrModal 
        showQrModal={showQrModal}
        setShowQrModal={setShowQrModal}
        setShowFullQrImage={setShowFullQrImage}
      />

      <FullQrImageModal 
        showFullQrImage={showFullQrImage}
        setShowFullQrImage={setShowFullQrImage}
      />
    </div>
  );
}

export default App;